import type { EffectTarget, SynergyEffect, SynergyStatus } from '../effects.js';
import type { EntityPool, Tower } from '../entities.js';
import { resetRunMods, resetTowerMods, type RunMods, type TowerMods } from '../mods.js';
import type { ClassSpec, ClassTable, RunEffect, TowerTable } from '../scenario.js';
import { SUPPORT_SURGE_FACTOR } from '../constants.js';
import { distSq } from '../math.js';
import { starRange } from './targeting.js';
import { CLASS_IDS, type ClassId, type EntityId, type StatusKind } from '../types.js';

/**
 * The synergy engine (TRD-08 Part A) — the system the game is actually about.
 *
 * Two rules carry almost all of the correctness here:
 *
 * **Recompute from scratch, never incrementally** (R-08-05). Every mod is reset to
 * identity and rebuilt from the current board. Incremental adjustment is where every
 * "I sold the tower but kept the buff" bug in this genre comes from, and they are
 * miserable to find because wrong state is indistinguishable from right state until
 * someone does the arithmetic by hand.
 *
 * **Adds before muls, per stat** (R-08-09). Effects are declared in a fixed order, but
 * `+5 then ×2` and `×2 then +5` differ, and a player reasoning about their board should
 * not have to know the declaration order of a content file. Accumulating separately and
 * resolving `(identity + Σadds) × Πmuls` makes stacking predictable, which matters more
 * than expressive ordering.
 */

/** Stats addressable by a `stat` or `aura` effect. */
type NumericTowerModKey = {
  [K in keyof TowerMods]: TowerMods[K] extends number ? K : never;
}[keyof TowerMods];

type NumericRunModKey = {
  [K in keyof RunMods]: RunMods[K] extends number ? K : never;
}[keyof RunMods];

/**
 * Identity per stat.
 *
 * Multiplicative stats rest at 1 (crit multiplier at 2), additive ones at 0. Resolving
 * every stat as `(identity + Σadds) × Πmuls` then works uniformly, with no per-field
 * special cases for the evaluator to get wrong.
 */
const IDENTITY: Record<NumericTowerModKey, number> = {
  flatDamage: 0,
  damageMultiplier: 1,
  attackSpeedMultiplier: 1,
  rangeBonus: 0,
  rangeMultiplier: 1,
  critChance: 0,
  critMultiplier: 2,
  armorPierce: 0,
  percentMaxHpDamage: 0,
  chainCount: 0,
  pierceCount: 0,
};

interface Accumulator {
  adds: Map<NumericTowerModKey, number>;
  muls: Map<NumericTowerModKey, number>;
}

/** Per-tick synergy behaviour that recompute discovers but cannot itself execute. */
export interface SynergyRuntime {
  statuses: SynergyStatus[];
  /** Warden's slow-field: a status applied to ENEMIES near towers of a class. */
  auraStatuses: {
    classId: ClassId;
    radiusTiles: number;
    status: StatusKind;
    magnitude: number;
    durationTicks: number;
    towers: EntityId[];
  }[];
  /** Frost-6's field freeze. Fired on absolute ticks so it never drifts (R-08-12). */
  periodics: { classId: ClassId; everyTicks: number; then: readonly SynergyEffect[] }[];
  /** Venom-4 retuning poison's cap and cadence; Frostbite stretching Chill. */
  statusTuning: Partial<
    Record<StatusKind, { cap?: number; tickIntervalTicks?: number; durationMultiplier?: number }>
  >;
  /** Swarm's drones, resolved per owning tower. */
  summons: Map<
    EntityId,
    { everyTicks: number; flatDamage: number; fromOwnerPercent: number; lifetimeTicks: number }
  >;
  /** Swarm-6's detonation, resolved per owning tower when one of its drones times out. */
  summonExpiry: Map<EntityId, { classId: ClassId; radiusTiles: number; damage: number }>;
  /** Alchemist-6's gold-on-kill. */
  goldOnKill: { amount: number; chance: number } | null;
  /**
   * Per-tower attribution of every `stat`/`aura` write, for the inspector's
   * "Damage 24 (+8 Beacon, ×1.2 Frost)" line (TRD-09 R-09-21). Recorded at the SAME
   * accumulator write that changes the mod, so the display can never drift from what
   * actually applied. Presentation data: not hashed, not replay-relevant.
   */
  statSources: Map<EntityId, { stat: string; op: 'add' | 'mul'; value: number; source: string }[]>;
}

export function emptyRuntime(): SynergyRuntime {
  return {
    statuses: [],
    auraStatuses: [],
    periodics: [],
    statusTuning: {},
    summons: new Map(),
    summonExpiry: new Map(),
    goldOnKill: null,
    statSources: new Map(),
  };
}

export interface SynergyContext {
  towers: EntityPool<Tower>;
  towerTable: TowerTable;
  /** Buff tiles lend a class to whoever stands on them — see `countClasses`. */
  classOnTile?: ((tileX: number, tileY: number) => ClassId | undefined) | undefined;
  /** The non-class half of a buff tile, as ordinary effects on the tower standing there. */
  tileEffectsFor?: ((tileX: number, tileY: number) => readonly SynergyEffect[] | undefined) | undefined;
  classTable: ClassTable;
  runMods: RunMods;
  /** Monoculture shifts every threshold (TRD-13 §6). Negative makes tiers easier. */
  thresholdShift?: number;
  /** Mutator + meta-tree bundles (R-08-22b). Applied BEFORE the class tier pass. */
  runEffects?: readonly RunEffect[];
  /**
   * The wave being fought and whether it is actually running, so a support tower's surge
   * can be read. A surge now lasts a whole ROUND, so it is live exactly when combat is
   * under way on the wave it was armed for.
   *
   * Absent = nothing is surging, which is what every non-World caller (tests, tools)
   * wants by default.
   */
  wave?: number | undefined;
  inCombat?: boolean | undefined;
}

/**
 * Count PLACED towers per class (R-08-01).
 *
 * Bench towers do not count. A ★3 contributes TWO; ★1 and ★2 contribute one each
 * (playtest request) — see the weight below for why the extra count stops at ★3.
 */
export function countClasses(
  towers: EntityPool<Tower>,
  towerTable: TowerTable,
  /** Resolves the class a buff tile grants at this tile, if any. */
  classOnTile?: (tileX: number, tileY: number) => ClassId | undefined,
): { counts: Map<ClassId, number>; contributors: Map<ClassId, EntityId[]> } {
  const counts = new Map<ClassId, number>();
  const contributors = new Map<ClassId, EntityId[]>();
  for (const id of CLASS_IDS) {
    counts.set(id, 0);
    contributors.set(id, []);
  }

  // Iterate the pool by index: insertion order is part of the determinism contract, and
  // `contributors` ends up in the UI where a reshuffling list would be visible.
  const items = towers.items;
  for (let i = 0; i < items.length; i++) {
    const tower = items[i] as Tower;
    if (!tower.alive) continue;
    const spec = towerTable[tower.defId];
    if (!spec) continue;

    // A ★3 is worth TWO of each of its classes (playtest request). ★1 and ★2 are worth
    // one, so the extra count lands only at the end of a nine-copy hunt.
    //
    // Deliberately not ★2 as well: that would put tier 6 within reach of three towers and
    // make the strongest board a small one, which is the concentration buff tiles exist
    // to break. At ★3 only, the reward is real and the price is finding nine copies.
    //
    // It also makes the second merge CHEAPER in class count than the first — three ★2s
    // counting 3 become one ★3 counting 2 — so chasing the third star no longer guts the
    // synergy that made the tower worth building.
    const weight = tower.star === 3 ? 2 : 1;
    for (const classId of spec.classes) {
      counts.set(classId, (counts.get(classId) ?? 0) + weight);
      // Once, whatever the weight: this list is "which towers light up when I hover the
      // row", and a tower highlighted twice is still one tower.
      contributors.get(classId)?.push(tower.id);
    }

    // A buff tile can lend the tower standing on it one more class.
    //
    // Counted here rather than written onto the tower, because a class is a property of
    // WHERE the tower stands: move it off the tile and the count has to fall again, and
    // a recompute reading position is the only version of that which cannot go stale.
    const granted = classOnTile?.(tower.tileX, tower.tileY);
    // The TILE grants the class, so it grants one — a ★3 standing on it does not make
    // the ground count double. The tile is the source; the tower is only the occupant.
    if (granted !== undefined && !spec.classes.includes(granted)) {
      counts.set(granted, (counts.get(granted) ?? 0) + 1);
      contributors.get(granted)?.push(tower.id);
    }
  }

  return { counts, contributors };
}

function activeTiers(spec: ClassSpec, count: number, shift: number): ClassSpec['tiers'][number][] {
  // Every tier at or below the count is active, not just the highest (R-08-03): at 5
  // Frost both the tier-2 and tier-4 effect sets apply.
  return spec.tiers.filter((tier) => tier.threshold + shift <= count);
}

export function activeTierFor(count: number, shift = 0): 0 | 2 | 4 | 6 {
  if (count >= 6 + shift) return 6;
  if (count >= 4 + shift) return 4;
  if (count >= 2 + shift) return 2;
  return 0;
}

export function nextThresholdFor(count: number, shift = 0): 2 | 4 | 6 | null {
  if (count < 2 + shift) return 2;
  if (count < 4 + shift) return 4;
  if (count < 6 + shift) return 6;
  return null;
}

/**
 * Rebuild every mod on the board from scratch.
 *
 * Runs only when the placed-tower set changed (R-08-04) — it is a no-op on the vast
 * majority of ticks, which is what keeps an O(towers × effects) pass affordable.
 */
export function recomputeSynergies(ctx: SynergyContext): SynergyRuntime {
  const shift = ctx.thresholdShift ?? 0;
  const runtime = emptyRuntime();

  const live: Tower[] = [];
  const items = ctx.towers.items;
  for (let i = 0; i < items.length; i++) {
    const tower = items[i] as Tower;
    if (tower.alive) live.push(tower);
  }

  // 1. Reset to identity. Everything below only ever adds. Assist bookkeeping resets
  //    with the mods — an aura source list that survived a recompute could credit a
  //    carrier for towers it stopped buffing (B-12 stretch).
  for (const tower of live) {
    resetTowerMods(tower.mods);
    tower.assistSources.length = 0;
  }
  resetRunMods(ctx.runMods);

  const { counts, contributors } = countClasses(ctx.towers, ctx.towerTable, ctx.classOnTile);

  const accumulators = new Map<EntityId, Accumulator>();
  const accFor = (tower: Tower): Accumulator => {
    let acc = accumulators.get(tower.id);
    if (!acc) {
      acc = { adds: new Map(), muls: new Map() };
      accumulators.set(tower.id, acc);
    }
    return acc;
  };

  const runAdds = new Map<NumericRunModKey, number>();
  const runMuls = new Map<NumericRunModKey, number>();
  const runSets = new Map<NumericRunModKey, number>();

  // 1b. Run-wide effect bundles — the daily mutator and the meta tree (R-08-22b) —
  //     BEFORE the class pass: meta modifies the rules, then the rules run. Order within
  //     the list is array order, which is authored data and therefore deterministic.
  //     A `forClass` bundle borrows that class's member set, so "Frost towers +10%"
  //     evaluates exactly like a Frost tier effect; a global bundle sees every tower.
  //     `thresholdShift` is deliberately NOT handled here — the World resolves it at
  //     construction, before any recompute could need it.
  for (const runEffect of ctx.runEffects ?? []) {
    const scoped = runEffect.forClass !== undefined;
    const members = scoped ? (contributors.get(runEffect.forClass as ClassId) ?? []) : [];
    const allIds = live.map((t) => t.id);
    for (const effect of runEffect.effects) {
      applyEffect(effect, {
        // Global bundles carry no class identity; the id below only labels runtime
        // entries (aura/periodic attribution) and CLASS_IDS[0] is the documented
        // stand-in. Authored global bundles stick to class-free kinds.
        classId: (runEffect.forClass ?? CLASS_IDS[0]) as ClassId,
        source: runEffect.label ?? 'Meta',
        live,
        memberSet: new Set(scoped ? members : allIds),
        members: scoped ? [...members] : allIds,
        accFor,
        runAdds,
        runMuls,
        runSets,
        runtime,
      });
    }
  }

  // 2. Apply, in a FIXED order: classes in CLASS_IDS declaration order, tiers ascending,
  //    effects in array order (R-08-08). `mul` does not commute with `add`, so this order
  //    is part of the determinism contract, not a stylistic choice.
  for (const classId of CLASS_IDS) {
    const spec = ctx.classTable[classId];
    if (!spec) continue;

    const count = counts.get(classId) ?? 0;
    const members = contributors.get(classId) ?? [];
    const memberSet = new Set(members);

    for (const tier of activeTiers(spec, count, shift)) {
      for (const effect of tier.effects) {
        applyEffect(effect, {
          classId,
          source: classId,
          live,
          memberSet,
          members,
          accFor,
          runAdds,
          runMuls,
          runSets,
          runtime,
        });
      }
    }

    runtime.statuses.push({
      classId,
      count,
      activeTier: activeTierFor(count, shift),
      nextThreshold: nextThresholdFor(count, shift),
      contributingTowers: members,
    });
  }

  // 2a-bis. Buff tiles (playtest request) — what the GROUND under a tower gives it.
  //
  // Evaluated exactly like an innate effect and for the same reason: the tower is the
  // sole member, so the buff lands on it alone. The class grant is handled in
  // `countClasses` instead, because a class is a count rather than a mod, and stars do
  // NOT scale any of this — the tile is a property of the map, not of the tower, so a ★3
  // has no more claim on it than a ★1.
  if (ctx.tileEffectsFor) {
    for (const tower of live) {
      const effects = ctx.tileEffectsFor(tower.tileX, tower.tileY);
      if (!effects || effects.length === 0) continue;
      const spec = ctx.towerTable[tower.defId];
      for (const effect of effects) {
        applyEffect(effect, {
          classId: (spec?.classes[0] ?? CLASS_IDS[0]) as ClassId,
          source: 'Ground',
          live,
          memberSet: new Set([tower.id]),
          members: [tower.id],
          accFor,
          runAdds,
          runMuls,
          runSets,
          runtime,
        });
      }
    }
  }

  // 2b. Innate tower effects (backlog B-15/B-17) — a spec's authored identity: a support
  //     tower's aura, a broodmother's summon. Each evaluates as if the tower were the
  //     sole member of its own class, so `sameClass` targets only the carrier and `aura`
  //     radiates from it alone. Deliberately AFTER the class pass: `stat`/`aura` writes
  //     accumulate commutatively so order is moot for them, but replace-semantics
  //     runtime entries (summon) must let the authored identity beat the generic class
  //     grant on its own tower — a broodmother's brood is not downgraded by Swarm-2.
  for (const tower of live) {
    const innate = ctx.towerTable[tower.defId]?.innateEffects;
    if (!innate || innate.length === 0) continue;
    const spec = ctx.towerTable[tower.defId];
    const memberSet = new Set([tower.id]);
    // Stars scale the innate effects the way they scale a fighter's damage — a ★2
    // support tower whose aura stayed at ★1 numbers would make merging one a pure loss
    // of two board copies (playtest report). ★1 is scale 1, so nothing changes there.
    const starScale = tower.starScaling[tower.star - 1] ?? 1;
    // A support tower's surge (playtest request): for a few seconds its aura pays out at
    // SUPPORT_SURGE_FACTOR times its passive bonus.
    //
    // It rides the SAME scaling path stars use, which is what keeps it honest — the
    // factor multiplies the bonus (1.05 → 1.20), never the multiplier itself (which would
    // give 4.2× and delete the wave). One function decides what "scaled" means for every
    // effect shape, so a new effect kind cannot quietly acquire two different answers.
    // The phase test is not redundant with the wave test: a surge spent DURING wave 7
    // leaves `surgeWave` at 7, and the build phase that follows is still wave 7 — without
    // it the aura would keep paying out between rounds, where it does nothing but read
    // as a bug on the tower's own panel.
    const surging = ctx.inCombat === true && ctx.wave === tower.surgeWave;
    const scale = surging ? starScale * SUPPORT_SURGE_FACTOR : starScale;
    for (const effect of innate) {
      applyEffect(scaleInnateEffect(effect, scale), {
        classId: (spec?.classes[0] ?? CLASS_IDS[0]) as ClassId,
        source: tower.defId,
        live,
        memberSet,
        members: [tower.id],
        accFor,
        runAdds,
        runMuls,
        runSets,
        runtime,
      });
    }
  }

  // 3. Resolve: (identity + Σadds) × Πmuls.
  for (const tower of live) {
    const acc = accumulators.get(tower.id);
    if (!acc) continue;
    for (const key of Object.keys(IDENTITY) as NumericTowerModKey[]) {
      const add = acc.adds.get(key) ?? 0;
      const mul = acc.muls.get(key) ?? 1;
      if (add === 0 && mul === 1) continue;
      tower.mods[key] = ((IDENTITY[key] ?? 0) + add) * mul;
    }
  }

  for (const [key, value] of runSets) ctx.runMods[key] = value;
  for (const [key, value] of runAdds) {
    if (!runSets.has(key)) ctx.runMods[key] += value;
  }
  for (const [key, value] of runMuls) ctx.runMods[key] *= value;

  // Sorted for the UI: strongest first, then by count, then alphabetically (R-08-14).
  // Stable, so a player's active synergies never reorder under them mid-build.
  runtime.statuses.sort(
    (a, b) =>
      b.activeTier - a.activeTier || b.count - a.count || a.classId.localeCompare(b.classId),
  );

  return runtime;
}

/**
 * Star-scale an innate effect's magnitude (B-15 follow-up).
 *
 * Additive values scale directly. Multiplicative values scale their BONUS — a ×1.10
 * damage aura at ★2 (scale 1.8) becomes ×1.18, never ×1.98, because multiplying a
 * multiplier scales the wrong thing (and would turn a penalty like ×0.8 into a buff).
 * A summon's flat damage scales here; the owner-damage share already scales at spawn
 * time, so the drone's whole damage ends up multiplied uniformly. Kinds with no
 * sensible magnitude pass through untouched.
 */
function scaleInnateEffect(effect: SynergyEffect, scale: number): SynergyEffect {
  if (scale === 1) return effect;
  switch (effect.k) {
    case 'stat':
    case 'aura':
      return effect.op === 'add'
        ? { ...effect, value: effect.value * scale }
        : { ...effect, value: 1 + (effect.value - 1) * scale };
    case 'auraStatus':
      return { ...effect, magnitude: effect.magnitude * scale };
    case 'summon':
      return { ...effect, flatDamage: effect.flatDamage * scale };
    default:
      // Includes `startingGold`, which is resolved once in the World's constructor and
      // has no per-tick meaning — see `startingGoldBonus`.
      return effect;
  }
}

interface ApplyCtx {
  classId: ClassId;
  /** Who granted the effect, for the inspector's breakdown (R-09-21): a classId for
   *  tier effects, a RunEffect label for mutator/meta bundles. Presentation-only. */
  source: string;
  live: Tower[];
  memberSet: Set<EntityId>;
  members: EntityId[];
  accFor: (tower: Tower) => Accumulator;
  runAdds: Map<NumericRunModKey, number>;
  runMuls: Map<NumericRunModKey, number>;
  runSets: Map<NumericRunModKey, number>;
  runtime: SynergyRuntime;
}

/** One breakdown line per accumulator write — same call site, so it cannot drift. */
function recordSource(
  ctx: ApplyCtx,
  towerId: EntityId,
  stat: string,
  op: 'add' | 'mul',
  value: number,
): void {
  let entries = ctx.runtime.statSources.get(towerId);
  if (!entries) {
    entries = [];
    ctx.runtime.statSources.set(towerId, entries);
  }
  entries.push({ stat, op, value, source: ctx.source });
}

function applyEffect(effect: SynergyEffect, ctx: ApplyCtx): void {
  switch (effect.k) {
    case 'stat': {
      const targets = resolveTargets(effect.target, ctx);
      for (const tower of targets) {
        const acc = ctx.accFor(tower);
        const key = effect.stat as NumericTowerModKey;
        if (effect.op === 'add') acc.adds.set(key, (acc.adds.get(key) ?? 0) + effect.value);
        else acc.muls.set(key, (acc.muls.get(key) ?? 1) * effect.value);
        recordSource(ctx, tower.id, key, effect.op, effect.value);
      }
      return;
    }

    case 'runStat': {
      const key = effect.stat as NumericRunModKey;
      if (effect.op === 'set') ctx.runSets.set(key, effect.value);
      else if (effect.op === 'add')
        ctx.runAdds.set(key, (ctx.runAdds.get(key) ?? 0) + effect.value);
      else ctx.runMuls.set(key, (ctx.runMuls.get(key) ?? 1) * effect.value);
      return;
    }

    case 'aura': {
      // Buffs OTHER towers within radius of each member — Beacon rewards tight packing,
      // and a self-buffing aura would just be a stat effect wearing a costume.
      const key = effect.stat as NumericTowerModKey;
      for (const source of ctx.live) {
        if (!ctx.memberSet.has(source.id)) continue;
        // B-2a: "adjacent" read as a lie next to a 4-tile attack range — Beacon auras
        // now cover the carrier's own printed range. `starRange`, deliberately: it grows
        // with merges (a merged support tower must cover MORE, or merging one does
        // nothing at all, since it fires nothing) while still excluding aura-granted
        // range, which could make one aura's reach depend on another's and turn the
        // answer into a question of evaluation order.
        const radius = effect.radiusFromRange ? starRange(source) : effect.radiusTiles;
        const radiusSq = radius * radius;
        for (const target of ctx.live) {
          if (target.id === source.id) continue;
          if (distSq(source.x, source.y, target.x, target.y) > radiusSq) continue;
          const acc = ctx.accFor(target);
          if (effect.op === 'add') acc.adds.set(key, (acc.adds.get(key) ?? 0) + effect.value);
          else acc.muls.set(key, (acc.muls.get(key) ?? 1) * effect.value);
          recordSource(ctx, target.id, key, effect.op, effect.value);
          // Assist attribution (B-12 stretch): the target now fights under this
          // carrier's aura, so the carrier earns a share of what the target deals.
          // Once per pair — a carrier granting two aura stats is still one assist.
          if (!target.assistSources.includes(source)) target.assistSources.push(source);
        }
      }
      return;
    }

    case 'auraStatus': {
      ctx.runtime.auraStatuses.push({
        classId: ctx.classId,
        radiusTiles: effect.radiusTiles,
        status: effect.status,
        magnitude: effect.magnitude,
        durationTicks: effect.durationTicks,
        towers: ctx.members,
      });
      return;
    }

    case 'periodic': {
      ctx.runtime.periodics.push({
        classId: ctx.classId,
        everyTicks: effect.everyTicks,
        then: effect.then,
      });
      return;
    }

    case 'statusTuning': {
      const existing = ctx.runtime.statusTuning[effect.status] ?? {};
      ctx.runtime.statusTuning[effect.status] = {
        ...existing,
        ...(effect.capOverride !== undefined ? { cap: effect.capOverride } : {}),
        ...(effect.tickIntervalTicks !== undefined
          ? { tickIntervalTicks: effect.tickIntervalTicks }
          : {}),
        ...(effect.durationMultiplier !== undefined
          ? { durationMultiplier: effect.durationMultiplier }
          : {}),
      };
      return;
    }

    case 'summon': {
      // REPLACE, not stack (see effects.ts). Tiers apply ascending, so the highest
      // reached tier wins — Swarm-4 upgrades Swarm-2's drone rather than running a second
      // emitter alongside it and hitting the drone cap on its own.
      for (const id of ctx.members) {
        ctx.runtime.summons.set(id, {
          everyTicks: effect.everyTicks,
          flatDamage: effect.flatDamage,
          fromOwnerPercent: effect.damageFromOwnerPercent,
          lifetimeTicks: effect.lifetimeTicks,
        });
      }
      return;
    }

    case 'gold': {
      if (effect.trigger === 'kill') {
        ctx.runtime.goldOnKill = { amount: effect.amount, chance: effect.chance ?? 1 };
      } else {
        ctx.runAdds.set('goldPerWave', (ctx.runAdds.get('goldPerWave') ?? 0) + effect.amount);
      }
      return;
    }

    // ── Hooks: collected onto the granting class's towers and evaluated inside the
    //    damage/fire pipeline (TRD-05 §6). Storing them rather than executing them here
    //    is what keeps recompute a pure function of the board.
    case 'applyStatus':
    case 'bonusVsStatus':
    case 'executeBelow':
    case 'onKillExplode':
    case 'onKillSpreadStatus':
    case 'everyNthShot':
    case 'chanceOnKill':
    case 'shotModifier': {
      const bucket =
        effect.k === 'onKillExplode' ||
        effect.k === 'onKillSpreadStatus' ||
        effect.k === 'chanceOnKill'
          ? 'onKillHooks'
          : effect.k === 'everyNthShot' || effect.k === 'shotModifier'
            ? 'onFireHooks'
            : 'onHitHooks';

      for (const tower of ctx.live) {
        if (!ctx.memberSet.has(tower.id)) continue;
        tower.mods[bucket].push(effect);
      }
      return;
    }

    case 'summonExpiryExplode': {
      // Same REPLACE shape as `summon`: the detonation belongs to the drone emitter, so
      // it is keyed by owning tower and the highest reached tier wins.
      for (const id of ctx.members) {
        ctx.runtime.summonExpiry.set(id, {
          classId: ctx.classId,
          radiusTiles: effect.radiusTiles,
          damage: effect.damage,
        });
      }
      return;
    }

    // Resolved by their owning systems, NOT here.
    //
    // `fieldStatus` runs inside its `periodic` parent, and `shopClassRestriction` and
    // `thresholdShift` are config-static — the World settles both at construction,
    // before any recompute could read them. Every kind in this list must be consumed
    // somewhere; `content.test.ts` asserts exactly that, because three of them once sat
    // here doing nothing and shipped that way.
    case 'fieldStatus':
    case 'shopClassRestriction':
    case 'thresholdShift':
      return;
  }
}

function resolveTargets(target: EffectTarget, ctx: ApplyCtx): Tower[] {
  switch (target.s) {
    case 'allTowers':
      return ctx.live;
    case 'sameClass':
      return ctx.live.filter((t) => ctx.memberSet.has(t.id));
    case 'adjacent': {
      const radiusSq = target.radiusTiles * target.radiusTiles;
      return ctx.live.filter((t) =>
        ctx.live.some(
          (source) =>
            ctx.memberSet.has(source.id) &&
            source.id !== t.id &&
            distSq(source.x, source.y, t.x, t.y) <= radiusSq,
        ),
      );
    }
  }
}
